import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileText, ClipboardCheck, BookOpen, Award, ArrowRight,
  BarChart2, Calendar, Clock, Download, Upload, CheckCircle, ExternalLink, Shield,
  User, CreditCard, GraduationCap, IdCard, TrendingUp, AlertCircle, Plus, X,
  MessageSquare, Send, Inbox, Reply, MapPin, Building2, Video
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import NotificationPreferences from '@/components/NotificationPreferences';
import { MyRegistrationsSection } from '@/components/MyRegistrationsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color, subtext }) {
  return (
    <Card className="imcac-card">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [marksheet, setMarksheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [results, setResults] = useState(null);
  
  // Messaging state
  const [messages, setMessages] = useState([]);
  const [showComposeMessage, setShowComposeMessage] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // Examination allocation state
  const [examinationSchedule, setExaminationSchedule] = useState(null);
  const [vivaSchedules, setVivaSchedules] = useState([]);
  const [osceSchedules, setOsceSchedules] = useState([]);
  
  // Payment state
  const [paymentHistory, setPaymentHistory] = useState({ payments: [], total_paid: 0 });
  const [pendingPayments, setPendingPayments] = useState({ pending_payments: [], total_pending: 0 });
  const [bankDetails, setBankDetails] = useState(null);
  const [showBankTransferModal, setShowBankTransferModal] = useState(null);
  const [bankTransferForm, setBankTransferForm] = useState({
    transfer_reference: '',
    transfer_date: '',
    bank_name: '',
    amount: 0,
    remarks: ''
  });

  // Handle URL parameters for tab selection
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'applications') {
      setActiveTab('applications');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, appsRes, certsRes, marksheetRes, messagesRes, scheduleRes, vivaRes, osceRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers }),
          axios.get(`${API}/applications`, { headers }),
          axios.get(`${API}/my-certificates`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/my-marksheet`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/portal/messages`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/student/examination-schedule`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/student/viva-schedules`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/student/osce-schedules`, { headers }).catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setApps(appsRes.data);
        setCertificates(certsRes.data || []);
        setMarksheet(marksheetRes.data);
        setMessages(messagesRes.data || []);
        setExaminationSchedule(scheduleRes.data?.[0] || null);
        setVivaSchedules(vivaRes.data || []);
        setOsceSchedules(osceRes.data || []);
        
        // Fetch enhanced profile
        fetchProfile();
        fetchResults();
        fetchPaymentHistory();
        fetchPendingPayments();
        fetchBankDetails();
        
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/student/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchResults = async () => {
    try {
      const res = await axios.get(`${API}/student/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPaymentHistory = async () => {
    try {
      const res = await axios.get(`${API}/student/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPendingPayments = async () => {
    try {
      const res = await axios.get(`${API}/student/pending-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPayments(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBankDetails = async () => {
    try {
      const res = await axios.get(`${API}/student/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankDetails(res.data);
    } catch (err) { console.error(err); }
  };

  const submitBankTransfer = async () => {
    if (!bankTransferForm.transfer_reference || !bankTransferForm.transfer_date || !bankTransferForm.bank_name) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/student/application/${showBankTransferModal.id}/bank-transfer`, {
        ...bankTransferForm,
        amount: showBankTransferModal.amount,
        application_id: showBankTransferModal.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bank transfer details submitted. Awaiting admin verification.');
      setShowBankTransferModal(null);
      setBankTransferForm({ transfer_reference: '', transfer_date: '', bank_name: '', amount: 0, remarks: '' });
      fetchPendingPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bank transfer');
    }
  };

  const downloadInvoice = async (applicationId) => {
    try {
      const res = await axios.get(`${API}/student/generate-invoice/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${applicationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error('Failed to download invoice');
    }
  };

  const handleDownloadIdCard = async () => {
    try {
      const response = await axios.get(`${API}/student/id-card`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'IMCAC_Student_ID.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('ID Card downloaded');
    } catch (err) {
      toast.error('Failed to download ID card');
    }
  };

  // Messaging functions
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.subject.trim() || !messageForm.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSendingMessage(true);
    try {
      await axios.post(`${API}/portal/messages`, {
        subject: messageForm.subject,
        message: messageForm.message,
        portal_type: 'candidate'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent to IMCAC Admin');
      setMessageForm({ subject: '', message: '' });
      setShowComposeMessage(false);
      // Refresh messages
      const res = await axios.get(`${API}/portal/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data || []);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const unreadCount = messages.filter(m => m.direction === 'outgoing' && m.status === 'unread').length;

  const statusColor = (s) => {
    const map = { 
      submitted: 'bg-blue-100 text-blue-700', 
      approved: 'bg-green-100 text-green-700', 
      rejected: 'bg-red-100 text-red-700', 
      pending: 'bg-yellow-100 text-yellow-700',
      exam_scheduled: 'bg-purple-100 text-purple-700',
      graded: 'bg-emerald-100 text-emerald-700'
    };
    return map[s] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading dashboard...</div>;

  // Prepare chart data
  const resultChartData = results?.overall_result ? [
    { name: 'MCQ', value: results.mcq_results?.[0]?.score || 0 },
    { name: 'Case Log', value: (results.case_logs?.average_score || 0) * 10 },
    { name: 'Viva', value: (results.viva_results?.[0]?.overall_score || 0) * 10 },
  ] : [];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="dashboard-page">
      <section className="bg-[#1B2A4A] py-10">
        <div className="imcac-container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="imcac-overline mb-2">Candidate Portal</p>
              <h1 className="text-3xl font-bold text-white font-heading">Welcome, {user?.full_name}</h1>
              <p className="text-sm text-slate-300 mt-1">Manage your examinations, logbook, and certification</p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleDownloadIdCard} 
                className="bg-[#C5A572] text-white rounded-sm text-sm"
                data-testid="download-id-card-btn"
              >
                <IdCard className="h-4 w-4 mr-1" /> Download ID Card
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={FileText} label="Applications" value={stats?.applications_count || 0} color="bg-blue-100 text-blue-600" />
          <StatCard icon={ClipboardCheck} label="Logbook Entries" value={stats?.logbook_entries_count || 0} color="bg-green-100 text-green-600" />
          <StatCard icon={BookOpen} label="Exams Completed" value={stats?.exams_completed || 0} color="bg-purple-100 text-purple-600" />
          <StatCard icon={Award} label="Certificates" value={certificates.length} color="bg-amber-100 text-amber-600" />
          <StatCard 
            icon={TrendingUp} 
            label="Latest Score" 
            value={stats?.latest_exam ? `${Math.round(stats.latest_exam.score)}%` : 'N/A'} 
            color="bg-emerald-100 text-emerald-600" 
          />
          <StatCard 
            icon={CheckCircle} 
            label="Result" 
            value={results?.overall_result?.final_result || 'Pending'} 
            color={results?.overall_result?.final_result === 'Pass' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'} 
          />
        </div>

        {/* Main Tabs */}
        <Card className="imcac-card">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1">
              <TabsTrigger value="overview" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <BarChart2 className="h-4 w-4 mr-1" /> Overview
              </TabsTrigger>
              <TabsTrigger value="applications" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-applications">
                <FileText className="h-4 w-4 mr-1" /> Applications
              </TabsTrigger>
              <TabsTrigger value="registrations" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-registrations">
                My Registrations
              </TabsTrigger>
              <TabsTrigger value="results" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-results">
                <TrendingUp className="h-4 w-4 mr-1" /> Results
              </TabsTrigger>
              <TabsTrigger value="certificates" className="rounded-sm text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white" data-testid="tab-certificates">
                <Award className="h-4 w-4 mr-1" /> Certificates
              </TabsTrigger>
              <TabsTrigger value="messages" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white relative" data-testid="tab-messages">
                <MessageSquare className="h-4 w-4 mr-1" /> Messages
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="fees" className="rounded-sm text-sm data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" data-testid="tab-fees">
                <CreditCard className="h-4 w-4 mr-1" /> Fees & Payments
              </TabsTrigger>
              <TabsTrigger value="profile" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-profile">
                <User className="h-4 w-4 mr-1" /> Profile
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="p-5">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Quick Actions */}
                <Card className="lg:col-span-1">
                  <CardContent className="p-4">
                    <h3 className="text-base font-semibold text-[#1B2A4A] mb-3">Quick Actions</h3>
                    <div className="space-y-2">
                      {/* New Exam Application */}
                      <Link to="/apply">
                        <div className="flex items-center gap-3 p-2 hover:bg-[#F8FAFC] rounded-sm transition-colors cursor-pointer border border-transparent hover:border-slate-200">
                          <FileText className="h-4 w-4 text-[#C5A572]" />
                          <span className="text-sm text-slate-700">New Exam Application</span>
                          <ArrowRight className="h-3 w-3 text-slate-400 ml-auto" />
                        </div>
                      </Link>
                      {/* Other quick actions */}
                      {[
                        { to: '/logbook', icon: Upload, label: 'Upload Case Log' },
                        { to: '/mcq', icon: BookOpen, label: 'Take MCQ Exam' },
                        { to: '/osce', icon: Calendar, label: 'View OSCE Schedule' },
                        { to: '/verification', icon: CheckCircle, label: 'Verify Certificate' },
                      ].map((action) => (
                        <Link key={action.to} to={action.to}>
                          <div className="flex items-center gap-3 p-2 hover:bg-[#F8FAFC] rounded-sm transition-colors cursor-pointer border border-transparent hover:border-slate-200">
                            <action.icon className="h-4 w-4 text-[#C5A572]" />
                            <span className="text-sm text-slate-700">{action.label}</span>
                            <ArrowRight className="h-3 w-3 text-slate-400 ml-auto" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Latest Result */}
                <Card className="lg:col-span-2">
                  <CardContent className="p-4">
                    <h3 className="text-base font-semibold text-[#1B2A4A] mb-3">Latest Examination Result</h3>
                    {results?.overall_result ? (
                      <div className="flex flex-wrap items-center gap-6">
                        <div className="text-center">
                          <p className="text-4xl font-bold text-[#1B2A4A] font-heading">
                            {results.overall_result.total_weighted_score?.toFixed(1) || 'N/A'}%
                          </p>
                          <p className="text-xs text-slate-500">Weighted Score</p>
                        </div>
                        <Separator orientation="vertical" className="h-16 hidden sm:block" />
                        <div>
                          <Badge className={`text-sm ${
                            results.overall_result.final_result === 'Pass' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {results.overall_result.final_result}
                          </Badge>
                          {results.overall_result.award_classification && (
                            <Badge className="ml-2 bg-[#C5A572] text-white text-xs">
                              {results.overall_result.award_classification}
                            </Badge>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            Submitted: {results.overall_result.submitted_at ? new Date(results.overall_result.submitted_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <TrendingUp className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No examination results yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Recent Applications */}
              <Card className="mt-6">
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold text-[#1B2A4A] mb-3">Recent Applications</h3>
                  {apps.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">No applications yet</p>
                      <Link to="/apply">
                        <Button className="mt-3 bg-[#1B2A4A] text-white rounded-sm text-sm">Apply Now</Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {apps.slice(0, 3).map((app) => (
                        <div key={app.id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-sm border border-slate-100">
                          <div>
                            <p className="text-sm font-medium text-[#1B2A4A]">{app.examination || app.specialty} - Route {app.route}</p>
                            <p className="text-xs text-slate-500">{new Date(app.created_at).toLocaleDateString()}</p>
                          </div>
                          <Badge className={`${statusColor(app.status)} text-xs rounded-sm`}>{app.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Examination Schedule - Shows if scheduled */}
              {examinationSchedule && (
                <Card className="mt-6 border-2 border-green-200 bg-gradient-to-r from-green-50 to-white" data-testid="examination-schedule-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="h-5 w-5 text-green-600" />
                      <h3 className="text-base font-semibold text-[#1B2A4A]">Your Scheduled Examination</h3>
                      <Badge className="bg-green-100 text-green-700 ml-auto">
                        {examinationSchedule.status === 'rescheduled' ? 'Rescheduled' : 'Scheduled'}
                      </Badge>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Date & Time */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-sm bg-blue-100 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1B2A4A]">{examinationSchedule.examination_date}</p>
                          <p className="text-xs text-slate-500">{examinationSchedule.examination_time || 'Time TBD'}</p>
                        </div>
                      </div>
                      {/* Examination Type */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-sm bg-purple-100 flex items-center justify-center">
                          <ClipboardCheck className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1B2A4A]">{examinationSchedule.examination_type}</p>
                          <p className="text-xs text-slate-500">{examinationSchedule.examination}</p>
                        </div>
                      </div>
                      {/* Center */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1B2A4A]">{examinationSchedule.examination_center?.name}</p>
                          <p className="text-xs text-slate-500 line-clamp-2">{examinationSchedule.examination_center?.address}</p>
                        </div>
                      </div>
                      {/* Examiner */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-sm bg-teal-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1B2A4A]">{examinationSchedule.examiner?.name}</p>
                          <p className="text-xs text-slate-500">Assigned Examiner</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Viva Schedules */}
              {vivaSchedules.length > 0 && (
                <Card className="imcac-card mb-4 border-2 border-purple-200">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 rounded-sm bg-purple-100 flex items-center justify-center">
                        <Video className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-[#1B2A4A]">Your Viva Examinations</h3>
                        <p className="text-xs text-slate-500">{vivaSchedules.length} scheduled</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {vivaSchedules.map(v => (
                        <div key={v.id} className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="h-4 w-4 text-purple-600" />
                                <p className="text-sm font-semibold text-[#1B2A4A]">
                                  {new Date(v.scheduled_date || v.exam_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <Clock className="h-4 w-4 text-purple-500" />
                                <p className="text-sm text-slate-600">{v.scheduled_time || v.exam_time} ({v.duration_minutes || 30} minutes)</p>
                              </div>
                              <p className="text-xs text-slate-500 mb-1">Examiner: <span className="font-medium">{v.examiner_name}</span></p>
                              <p className="text-xs text-slate-500">
                                {v.is_online ? (
                                  <span className="text-purple-600 font-medium">Online Examination</span>
                                ) : (
                                  <span>Location: {v.location || 'TBD'}</span>
                                )}
                              </p>
                              {v.notes && <p className="text-xs text-slate-400 mt-1 italic">Note: {v.notes}</p>}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className={`text-xs ${v.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                                {v.status || 'Scheduled'}
                              </Badge>
                              {v.meeting_link && (
                                <a href={v.meeting_link} target="_blank" rel="noopener noreferrer">
                                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm text-xs">
                                    <Video className="h-3 w-3 mr-1" /> Join Google Meet
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                          {v.meeting_link && (
                            <div className="mt-3 pt-3 border-t border-purple-200">
                              <p className="text-xs text-slate-500 mb-1">Meeting Link:</p>
                              <a href={v.meeting_link} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-600 hover:underline break-all">
                                {v.meeting_link}
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* OSCE Schedules */}
              {osceSchedules.length > 0 && (
                <Card className="imcac-card mb-4 border-2 border-teal-200">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 rounded-sm bg-teal-100 flex items-center justify-center">
                        <ClipboardCheck className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-[#1B2A4A]">Your OSCE Examinations</h3>
                        <p className="text-xs text-slate-500">{osceSchedules.length} scheduled</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {osceSchedules.map(o => (
                        <div key={o.id} className="p-4 bg-teal-50 rounded-lg border border-teal-100">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="h-4 w-4 text-teal-600" />
                                <p className="text-sm font-semibold text-[#1B2A4A]">
                                  {new Date(o.exam_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <Clock className="h-4 w-4 text-teal-500" />
                                <p className="text-sm text-slate-600">{o.exam_time}</p>
                              </div>
                              <p className="text-xs text-slate-500 mb-1">Examiner: <span className="font-medium">{o.examiner_name}</span></p>
                              <div className="flex items-center gap-1 text-xs text-slate-500">
                                <MapPin className="h-3 w-3" />
                                <span>{o.location || 'Location to be confirmed'}</span>
                              </div>
                              {o.stations_count && <p className="text-xs text-slate-400 mt-1">{o.stations_count} OSCE stations</p>}
                              {o.notes && <p className="text-xs text-slate-400 mt-1 italic">Note: {o.notes}</p>}
                            </div>
                            <Badge className={`text-xs ${o.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-teal-100 text-teal-700'}`}>
                              {o.status || 'Scheduled'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* My Registrations Tab */}
            <TabsContent value="registrations" className="p-5">
              <MyRegistrationsSection token={token} />
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications" className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Your Applications</h3>
                <div className="flex gap-2">
                  <Link to="/apply">
                    <Button className="bg-[#1B2A4A] text-white rounded-sm text-sm">
                      <Plus className="h-4 w-4 mr-1" /> New Exam Application
                    </Button>
                  </Link>
                </div>
              </div>
              
              {/* Regular Examination Applications */}
              <h4 className="text-base font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                <GraduationCap className="h-4 w-4" />
                Examination Applications
              </h4>
              
              {apps.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No examination applications yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {apps.map((app) => (
                    <div key={app.id} className="p-4 bg-white border border-slate-200 rounded-sm hover:border-[#C5A572] transition-colors">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-[#1B2A4A]">{app.examination || app.specialty}</h4>
                          <p className="text-sm text-slate-600">Route {app.route}</p>
                          <p className="text-xs text-slate-500 mt-1">Submitted: {new Date(app.created_at).toLocaleDateString()}</p>
                          {app.fee_amount && (
                            <p className="text-xs text-slate-500">Fee: ₹{app.fee_amount.toLocaleString()} {app.fee_paid ? '(Paid)' : '(Pending)'}</p>
                          )}
                        </div>
                        <Badge className={`${statusColor(app.status)} text-xs rounded-sm`}>
                          {app.status === 'exam_scheduled' ? 'Exam Scheduled' : app.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Results Tab */}
            <TabsContent value="results" className="p-5" data-testid="results-tab-content">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Examination Results</h3>
              
              {/* Overall Result Card */}
              {results?.overall_result && (
                <Card className="mb-6 border-l-4 border-l-[#C5A572]">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h4 className="text-sm text-slate-500 mb-1">Final Result</h4>
                        <div className="flex items-center gap-3">
                          <span className="text-3xl font-bold text-[#1B2A4A]">
                            {results.overall_result.total_weighted_score?.toFixed(1)}%
                          </span>
                          <Badge className={`text-base ${
                            results.overall_result.final_result === 'Pass' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-red-500 text-white'
                          }`}>
                            {results.overall_result.final_result}
                          </Badge>
                        </div>
                        {results.overall_result.award_classification && (
                          <Badge className="mt-2 bg-[#1B2A4A] text-white">
                            {results.overall_result.award_classification}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Component Scores */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4 text-center">
                    <BookOpen className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold text-[#1B2A4A]">
                      {results?.mcq_results?.[0]?.score?.toFixed(0) || '-'}%
                    </p>
                    <p className="text-xs text-slate-500">MCQ Score</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <ClipboardCheck className="h-8 w-8 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-[#1B2A4A]">
                      {results?.case_logs?.average_score?.toFixed(1) || '-'}/10
                    </p>
                    <p className="text-xs text-slate-500">Case Log Average</p>
                    <p className="text-xs text-slate-400">{results?.case_logs?.total || 0} logs</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Calendar className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                    <p className="text-2xl font-bold text-[#1B2A4A]">
                      {results?.comprehensive_scores?.[0]?.osce_score?.toFixed(0) || '-'}%
                    </p>
                    <p className="text-xs text-slate-500">OSCE Score</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <User className="h-8 w-8 mx-auto mb-2 text-amber-600" />
                    <p className="text-2xl font-bold text-[#1B2A4A]">
                      {results?.viva_results?.[0]?.overall_score?.toFixed(1) || '-'}/10
                    </p>
                    <p className="text-xs text-slate-500">Viva Score</p>
                  </CardContent>
                </Card>
              </div>

              {!results?.overall_result && (
                <div className="text-center py-12 text-slate-500">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No examination results available yet</p>
                  <p className="text-sm mt-1">Results will appear here once your examination is complete</p>
                </div>
              )}
            </TabsContent>

            {/* Certificates Tab */}
            <TabsContent value="certificates" className="p-5" data-testid="certificates-tab-content">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">My Certifications</h3>
              
              {certificates.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Shield className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No certifications yet</p>
                  <p className="text-sm mt-1">Complete your examination to receive your IMCAC credential</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {certificates.map((cert) => (
                    <div key={cert.id} className={`p-4 rounded-sm border-2 ${
                      cert.status === 'valid' ? 'border-green-200 bg-green-50' : 
                      cert.status === 'suspended' ? 'border-amber-200 bg-amber-50' : 
                      'border-red-200 bg-red-50'
                    }`}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-lg font-semibold text-[#1B2A4A]">{cert.certification_title}</p>
                            <Badge className={`text-xs ${
                              cert.classification === 'Certified with Distinction' ? 'bg-[#1B2A4A] text-white' :
                              cert.classification === 'Certified with Merit' ? 'bg-[#C5A572] text-white' :
                              'bg-green-600 text-white'
                            }`}>
                              {cert.classification}
                            </Badge>
                          </div>
                          <p className="text-sm font-mono text-slate-600">{cert.certificate_number}</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                            <span>Issued: {new Date(cert.issue_date).toLocaleDateString()}</span>
                            <span>Expires: {new Date(cert.expiry_date).toLocaleDateString()}</span>
                            <span>Route {cert.route}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${
                            cert.status === 'valid' ? 'bg-green-100 text-green-700' :
                            cert.status === 'suspended' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {cert.status === 'valid' ? '✓ Valid' : cert.status}
                          </Badge>
                          
                          <a 
                            href={`/verification?certificate=${cert.certificate_number}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#1B2A4A] bg-white border border-slate-200 rounded-sm hover:bg-slate-50"
                          >
                            <ExternalLink className="h-3 w-3" /> Verify
                          </a>
                          
                          <Link to="/transcript">
                            <Button size="sm" className="h-8 px-3 text-xs rounded-sm bg-[#1B2A4A] text-white">
                              <Download className="h-3 w-3 mr-1" /> Transcript
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="p-5" data-testid="messages-tab-content">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Messages</h3>
                  <p className="text-sm text-slate-500">Communicate with IMCAC Administration</p>
                </div>
                <Button 
                  onClick={() => setShowComposeMessage(true)}
                  className="bg-[#1B2A4A] text-white rounded-sm"
                  data-testid="compose-message-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> New Message
                </Button>
              </div>

              {/* Compose Message Modal */}
              {showComposeMessage && (
                <Card className="mb-6 border-2 border-[#C5A572]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Send className="h-4 w-4 text-[#C5A572]" /> Compose New Message
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setShowComposeMessage(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSendMessage} className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium">Subject *</Label>
                        <Input
                          placeholder="Enter message subject"
                          value={messageForm.subject}
                          onChange={(e) => setMessageForm({...messageForm, subject: e.target.value})}
                          required
                          className="mt-1 rounded-sm"
                          data-testid="message-subject"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Message *</Label>
                        <Textarea
                          placeholder="Type your message here..."
                          value={messageForm.message}
                          onChange={(e) => setMessageForm({...messageForm, message: e.target.value})}
                          required
                          rows={5}
                          className="mt-1 rounded-sm"
                          data-testid="message-body"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          type="submit" 
                          disabled={sendingMessage}
                          className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                          data-testid="send-message-btn"
                        >
                          <Send className="h-4 w-4 mr-1" /> {sendingMessage ? 'Sending...' : 'Send Message'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setShowComposeMessage(false)}
                          className="rounded-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Messages List */}
              {messages.length === 0 ? (
                <Card className="text-center py-12">
                  <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No messages yet</p>
                  <p className="text-sm text-slate-400 mt-1">Start a conversation with IMCAC Admin</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <Card 
                      key={msg.id} 
                      className={`cursor-pointer hover:border-[#C5A572] transition-colors ${
                        selectedMessage?.id === msg.id ? 'border-[#C5A572] border-2' : ''
                      } ${msg.direction === 'outgoing' && msg.status === 'unread' ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedMessage(selectedMessage?.id === msg.id ? null : msg)}
                      data-testid={`message-${msg.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              msg.direction === 'incoming' ? 'bg-[#1B2A4A]' : 'bg-[#C5A572]'
                            }`}>
                              {msg.direction === 'incoming' ? (
                                <Send className="h-4 w-4 text-white" />
                              ) : (
                                <Reply className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-[#1B2A4A]">{msg.subject}</p>
                              <p className="text-xs text-slate-500">
                                {msg.direction === 'incoming' ? 'You → Admin' : 'Admin → You'} • {new Date(msg.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <Badge className={`rounded-sm text-xs ${
                            msg.status === 'unread' ? 'bg-blue-100 text-blue-700' :
                            msg.status === 'replied' ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {msg.status === 'replied' ? 'Replied' : msg.status === 'unread' ? 'New' : 'Read'}
                          </Badge>
                        </div>
                        
                        {selectedMessage?.id === msg.id && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Fees & Payments Tab */}
            <TabsContent value="fees" className="p-5" data-testid="fees-tab-content">
              <div className="space-y-6">
                {/* Pending Payments */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="h-4 w-4" /> Pending Payments
                    </CardTitle>
                    <p className="text-xs text-slate-500">Pay your examination fees</p>
                  </CardHeader>
                  <CardContent>
                    {pendingPayments.pending_payments?.length > 0 ? (
                      <div className="space-y-3">
                        {pendingPayments.pending_payments.filter(p => p.status !== 'pending_verification').map(payment => (
                          <div key={payment.id} className="p-4 border border-green-200 bg-green-50/50 rounded-sm">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-[#1B2A4A]">{payment.description}</p>
                                  <Badge variant="outline" className="text-xs">{payment.type}</Badge>
                                </div>
                                <p className="text-xs text-slate-500">Applied: {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-600 text-lg">₹{payment.amount?.toLocaleString()}</p>
                              </div>
                            </div>
                            
                            {/* Payment Method Selection */}
                            <div className="flex gap-2 mt-3 pt-3 border-t border-green-200">
                              <Button
                                className="flex-1 bg-[#22c55e] hover:bg-[#16a34a] text-white text-xs rounded-sm"
                                onClick={() => window.open(`/api/payments/create/${payment.id}`, '_blank')}
                              >
                                <CreditCard className="h-3 w-3 mr-1" /> Pay Online (Razorpay)
                              </Button>
                              <Button
                                variant="outline"
                                className="flex-1 text-xs rounded-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white"
                                onClick={() => {
                                  setBankTransferForm({ ...bankTransferForm, amount: payment.amount });
                                  setShowBankTransferModal(payment);
                                }}
                              >
                                <Building2 className="h-3 w-3 mr-1" /> Bank Transfer
                              </Button>
                            </div>
                          </div>
                        ))}

                        {/* Pending Verification */}
                        {pendingPayments.pending_payments.filter(p => p.status === 'pending_verification').length > 0 && (
                          <div className="mt-4">
                            <h5 className="text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
                              <Clock className="h-4 w-4" /> Pending Verification ({pendingPayments.pending_payments.filter(p => p.status === 'pending_verification').length})
                            </h5>
                            <div className="space-y-2">
                              {pendingPayments.pending_payments.filter(p => p.status === 'pending_verification').map(payment => (
                                <div key={payment.id} className="flex items-center justify-between p-3 border border-blue-200 bg-blue-50/50 rounded-sm">
                                  <div>
                                    <p className="font-medium text-[#1B2A4A]">{payment.description}</p>
                                    <p className="text-xs text-slate-500">₹{payment.amount?.toLocaleString()}</p>
                                    {payment.bank_transfer && (
                                      <p className="text-xs text-blue-600 mt-1">Ref: {payment.bank_transfer.reference} | {payment.bank_transfer.date}</p>
                                    )}
                                  </div>
                                  <Badge className="bg-blue-100 text-blue-700">Awaiting Verification</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-green-50 rounded-sm">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <p className="text-sm text-green-700">All fees paid. No pending payments.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Payment History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Payment History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {paymentHistory.payments?.length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-[#F8FAFC]">
                                <th className="text-left p-3 text-xs font-bold">Description</th>
                                <th className="text-left p-3 text-xs font-bold">Type</th>
                                <th className="text-left p-3 text-xs font-bold">Date</th>
                                <th className="text-left p-3 text-xs font-bold">Payment ID</th>
                                <th className="text-left p-3 text-xs font-bold">Method</th>
                                <th className="text-right p-3 text-xs font-bold">Amount</th>
                                <th className="text-left p-3 text-xs font-bold">Invoice</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentHistory.payments.map((payment) => (
                                <tr key={payment.id} className="border-b border-slate-100">
                                  <td className="p-3">{payment.description}</td>
                                  <td className="p-3"><Badge className="bg-slate-100 text-slate-700 text-xs">{payment.type}</Badge></td>
                                  <td className="p-3 text-slate-500">{payment.date ? new Date(payment.date).toLocaleDateString() : '-'}</td>
                                  <td className="p-3 font-mono text-xs text-slate-500">{payment.payment_id}</td>
                                  <td className="p-3">
                                    <Badge className={payment.payment_method === 'bank_transfer' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                                      {payment.payment_method === 'bank_transfer' ? 'Bank Transfer' : 'Razorpay'}
                                    </Badge>
                                  </td>
                                  <td className="p-3 text-right font-semibold text-green-600">₹{payment.amount?.toLocaleString()}</td>
                                  <td className="p-3">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 px-2 text-xs rounded-sm"
                                      onClick={() => downloadInvoice(payment.application_id)}
                                    >
                                      <Download className="h-3 w-3 mr-1" /> Invoice
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-4 p-4 bg-[#1B2A4A] text-white rounded-sm flex justify-between items-center">
                          <span className="text-sm">Total Paid</span>
                          <span className="text-xl font-bold">₹{paymentHistory.total_paid?.toLocaleString()}</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No payment history yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Bank Details Card */}
                {bankDetails && (
                  <Card className="border-l-4 border-l-[#1B2A4A]">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="h-4 w-4" /> IMCAC Bank Details for Transfer
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Bank Name</p>
                          <p className="font-medium">{bankDetails.bank_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Branch</p>
                          <p className="font-medium">{bankDetails.branch}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Account Name</p>
                          <p className="font-medium">{bankDetails.account_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Account Number</p>
                          <p className="font-medium font-mono">{bankDetails.account_number}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">IFSC Code</p>
                          <p className="font-medium font-mono">{bankDetails.ifsc_code}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">UPI ID</p>
                          <p className="font-medium font-mono">{bankDetails.upi_id}</p>
                        </div>
                      </div>
                      <p className="text-xs text-amber-600 mt-4 p-2 bg-amber-50 rounded">
                        <AlertCircle className="h-3 w-3 inline mr-1" />
                        {bankDetails.notes}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Bank Transfer Modal */}
            {showBankTransferModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBankTransferModal(null)}>
                <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Submit Bank Transfer Details</h3>
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-sm">
                        <p className="text-sm font-medium">{showBankTransferModal.description}</p>
                        <p className="text-xs text-slate-500">Amount: ₹{showBankTransferModal.amount?.toLocaleString()}</p>
                      </div>
                      
                      {bankDetails && (
                        <div className="p-3 bg-blue-50 rounded-sm text-xs">
                          <p className="font-semibold text-blue-700 mb-1">Transfer to:</p>
                          <p>{bankDetails.account_name}</p>
                          <p>A/C: {bankDetails.account_number} | IFSC: {bankDetails.ifsc_code}</p>
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-sm">Transfer Reference / UTR *</Label>
                        <Input
                          value={bankTransferForm.transfer_reference}
                          onChange={e => setBankTransferForm({...bankTransferForm, transfer_reference: e.target.value})}
                          placeholder="Enter UTR or transaction reference"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Transfer Date *</Label>
                        <Input
                          type="date"
                          value={bankTransferForm.transfer_date}
                          onChange={e => setBankTransferForm({...bankTransferForm, transfer_date: e.target.value})}
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Bank Name *</Label>
                        <Input
                          value={bankTransferForm.bank_name}
                          onChange={e => setBankTransferForm({...bankTransferForm, bank_name: e.target.value})}
                          placeholder="e.g., HDFC Bank, SBI"
                          className="rounded-sm mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Remarks (Optional)</Label>
                        <Input
                          value={bankTransferForm.remarks}
                          onChange={e => setBankTransferForm({...bankTransferForm, remarks: e.target.value})}
                          placeholder="Any additional notes"
                          className="rounded-sm mt-1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-6">
                      <Button onClick={submitBankTransfer} className="bg-[#1B2A4A] text-white rounded-sm">
                        Submit Transfer Details
                      </Button>
                      <Button variant="outline" onClick={() => setShowBankTransferModal(null)} className="rounded-sm">Cancel</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Profile Tab */}
            <TabsContent value="profile" className="p-5" data-testid="profile-tab-content">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="h-4 w-4 text-[#C5A572]" /> Personal Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Full Name</Label>
                      <p className="text-sm font-medium">{user?.full_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Email</Label>
                      <p className="text-sm font-medium">{user?.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Student ID</Label>
                      <p className="text-sm font-mono">{profile?.student_profile?.student_id || 'Not assigned'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Registered Since</Label>
                      <p className="text-sm">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <IdCard className="h-4 w-4 text-[#C5A572]" /> ID Card & Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600">Download your official IMCAC Student ID card for verification purposes.</p>
                    <Button 
                      onClick={handleDownloadIdCard} 
                      className="w-full bg-[#1B2A4A] text-white rounded-sm"
                    >
                      <Download className="h-4 w-4 mr-1" /> Download Student ID Card (PDF)
                    </Button>
                    
                    {profile?.enrollment && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-sm">
                        <p className="text-xs text-blue-700 font-medium">Enrolled via Examination Center</p>
                        <p className="text-sm text-blue-800">{profile.enrollment.center_name}</p>
                        <p className="text-xs text-blue-600 mt-1">10% discount applied</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Notification Preferences */}
                <div className="md:col-span-2">
                  <NotificationPreferences />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Legal Footer */}
        <p className="text-xs text-slate-400 text-center mt-8 px-4">
          IMCAC examinations assess competency-based educational standards. Certification does not substitute statutory licensure. 
          Candidates must comply with local regulatory laws.
        </p>
      </div>
    </div>
  );
}
